import { Fragment, type ReactNode } from 'react';
import { Bug, Info, Plus, RefreshCw, type LucideIcon } from 'lucide-react';
import type { ChangelogEntryDto, ChangelogKind } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/shared/lib/cn';
import { formatDate } from '@/shared/lib/format';

const KIND: Record<ChangelogKind, { icon: LucideIcon; pill: string }> = {
  added: { icon: Plus, pill: 'bg-success-soft text-success' },
  changed: { icon: RefreshCw, pill: 'bg-info-soft text-info' },
  fixed: { icon: Bug, pill: 'bg-warning-soft text-warning' },
  note: { icon: Info, pill: 'bg-muted text-muted-foreground' },
};

/** Proste znakowanie inline z CHANGELOG.md: **pogrubienie**, *kursywa*, `kod`. */
export function renderInline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2)
      return (
        <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground">
          {part.slice(1, -1)}
        </code>
      );
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

/**
 * Wpisy mają postać „**Aplikacja — Pulpit:** opis”. Zapowiedź (pogrubienie na początku) wyciągamy do osobnego wiersza:
 * pierwszy człon przed „ — ” (Aplikacja / Panel / API…) jako plakietka, reszta jako tytuł; opis zostaje w akapicie.
 */
function splitLead(item: string): { area: string | null; title: string | null; body: string } {
  const m = item.match(/^\*\*([^*]+?)\*\*\s*([\s\S]*)$/);
  if (!m) return { area: null, title: null, body: item };
  const lead = m[1]!.replace(/\s*:\s*$/, '').trim();
  const body = (m[2] ?? '').replace(/^:\s*/, '').trim();
  const [first, ...rest] = lead.split(' — ');
  return rest.length ? { area: first!.trim(), title: rest.join(' — ').trim(), body } : { area: null, title: lead, body };
}

function ChangelogItem({ item }: { item: string }) {
  const { area, title, body } = splitLead(item);
  return (
    <li className="rounded-xl border border-border/70 bg-muted/40 px-3 py-2.5">
      {title && (
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          {area && <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-primary">{area}</span>}
          <span className="text-[13px] font-bold leading-snug text-foreground">{title}</span>
        </div>
      )}
      {body && <p className="text-[13px] leading-relaxed text-muted-foreground">{renderInline(body)}</p>}
    </li>
  );
}

/** Lista wpisów dziennika zmian (najnowszy pierwszy) — używana w oknie „Co nowego” i w Pomocy. */
export function ChangelogView({ entries, compact }: { entries: ChangelogEntryDto[]; compact?: boolean }) {
  const { t, locale } = useI18n();
  if (!entries.length) return <p className="py-6 text-center text-sm text-muted-foreground">{t('appu.changelog.empty')}</p>;
  return (
    <div className="space-y-6">
      {entries.map((entry, index) => (
        <section key={entry.version} aria-label={`v${entry.version}`} className={cn(index > 0 && 'border-t border-border pt-6')}>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3 className={cn('rounded-lg px-2.5 py-1 text-[13px] font-extrabold tabular-nums', index === 0 ? 'brand-gradient text-white shadow-sm' : 'bg-muted text-foreground')}>v{entry.version}</h3>
            {index === 0 && <span className="rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-bold text-success">{t('appu.changelog.latest')}</span>}
            <span className="ml-auto text-xs text-muted-foreground">{formatDate(entry.date, locale, 'd MMM yyyy')}</span>
          </div>
          {entry.title && <p className="mt-2 text-sm font-semibold">{entry.title}</p>}
          {entry.summary && <p className="mt-1 text-sm text-muted-foreground">{renderInline(entry.summary)}</p>}
          <div className={cn('mt-3 space-y-4', compact && 'space-y-3')}>
            {entry.sections.map((section) => {
              const { icon: Icon, pill } = KIND[section.kind];
              return (
                <div key={section.kind}>
                  <h4 className={cn('mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide', pill)}>
                    <Icon className="size-3" aria-hidden />
                    {t(`appu.changelog.kind.${section.kind}`)}
                  </h4>
                  <ul className="space-y-2">
                    {section.items.map((item, i) => (
                      <ChangelogItem key={i} item={item} />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
